import { useTableSortingAndPagination } from "#redux/reducers/tableStateReducer";
import { linkToDeviceDetail } from "#routers/links";
import { RoutingLink } from "#src/batteries-included-components/RoutingLink";
import { useListDevices } from "#src/components/hooks/adapters/utils/useDevices";
import { linkToFacilityDetail } from "#src/routes/organization/facilities/[facilityId]";
import {
  DataTablePanel,
  HeaderType,
  ItemAction,
  Pill,
  StorageKeys,
  useFilters,
} from "@validereinc/common-components";
import { DeviceType, SortDirection } from "@validereinc/domain";
import { toFlattenedObject, toTitleCaseString } from "@validereinc/utilities";
import React from "react";

export const DevicesTablePanel = ({
  filterConfigStorageKey,
  tableConfigStorageKey,
  tableStateStorageKey,
  selectedDevices,
  onSelectDevice,
  onDeselectDevice,
}: {
  selectedDevices: DeviceType[];
  onSelectDevice: (newDevice: DeviceType) => void;
  onDeselectDevice: (id: string) => void;
} & Pick<
  StorageKeys,
  "filterConfigStorageKey" | "tableConfigStorageKey" | "tableStateStorageKey"
>) => {
  const [filters] = useFilters(filterConfigStorageKey);
  const [tableState, updateTableState] = useTableSortingAndPagination(
    {
      sortBy: "created_at",
      sortDirection: SortDirection.DESCENDING,
    },
    filters,
    tableStateStorageKey
  );

  const params = {
    page: tableState.page,
    pageSize: tableState.pageSize,
    sort_by: tableState.sortBy,
    sort_direction: tableState.sortDirection,
    filters: toFlattenedObject(filters),
  };

  const { data, isLoading, isFetching } = useListDevices(params, {
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  const headers: Array<HeaderType<DeviceType>> = [
    {
      key: "name",
      label: "Name",
      renderComponent: ({ item }) =>
        item.id && item.name ? (
          <RoutingLink to={linkToDeviceDetail(item.id)}>
            {item.name}
          </RoutingLink>
        ) : (
          "-"
        ),
    },
    {
      key: "type.name",
      label: "Type",
    },
    {
      key: "facility.name",
      label: "Facility",
      renderComponent: ({ item }) =>
        item.facility_id && item.facility.name ? (
          <RoutingLink to={linkToFacilityDetail(item.facility_id)}>
            {item.facility.name}
          </RoutingLink>
        ) : (
          "-"
        ),
    },
    {
      key: "status",
      label: "Status",
      renderComponent: ({ item }) => (
        <Pill variant={item.status === "active" ? "success" : "default"}>
          {toTitleCaseString(item.status, {
            intent: "ui",
            exceptions: ["oil-and-gas"],
          })}
        </Pill>
      ),
    },
  ];

  const items = (data as any)?.data ?? [];
  return (
    <DataTablePanel
      storageKey={tableConfigStorageKey}
      panelProps={{
        title: "Devices",
      }}
      dataTableProps={{
        intent: "simplicity-first",
        items,
        getItemId: (item) => item.id,
        headers,
        isLoading,
        isBusy: isFetching,
        sorting: {
          sortBy: tableState.sortBy,
          sortDirection: tableState.sortDirection,
        },
        pagination: {
          page: tableState.page,
          pageSize: tableState.pageSize,
          total: (data as any)?.total_entries,
        },
        onPaginationChange: updateTableState,
        onSortChange: updateTableState,
        getItemActions: ({ item }: { item: DeviceType }): ItemAction[] =>
          selectedDevices?.find((selected) => selected.id === item.id)
            ? [
                {
                  label: "Remove",
                  buttonProps: {
                    onClick: () => onDeselectDevice(item?.id),
                    icon: "trash",
                  },
                },
              ]
            : [
                {
                  label: "Add",
                  buttonProps: {
                    onClick: () => onSelectDevice(item),
                    icon: "plus-circle",
                  },
                },
              ],
      }}
    />
  );
};
